import {useEffect, useState} from 'react'
import {useParams, useNavigate} from 'react-router-dom'
import TicketsService from '../../../../services/ticket.service'
import {KTCard, KTCardBody} from '../../../../_metronic/helpers'

type Attachments = {
  id: string
  fileName: string
  path: string
}

export type User = {
  id: string
  email: string
  phoneNumber: string | null
  fullName: string
  roles: string[]
  authService: string
}

type TicketComment = {
  id: string
  text: string
  createdAt: string
  updatedAt: string
  userId: string
  attachments?: Attachments[]
  ticketId: string
  user?: User
}

type TicketDetailsType = {
  id: string
  title: string
  description: string
  priority: string
  productType: string
  statusName: string
  createdAt: string
  updatedAt: string
  closedAt: string
  userId: string
  providerId: number
  orderId: string
  createrId: string
  assignedTo: string
  comments: TicketComment[]
}

const TicketDetails = () => {
  const {id} = useParams<{id: string}>()
  const navigate = useNavigate()
  const [ticket, setTicket] = useState<TicketDetailsType | null>(null)
  const [comments, setComments] = useState<TicketComment[]>([])
  const [loading, setLoading] = useState(false)
  const [commentText, setCommentText] = useState('')
  const [actorsSelect, setActorsSelect] = useState<string[]>([])
  const [files, setFiles] = useState<FileList | null>(null)
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null)
  const [editingActors, setEditingActors] = useState<string[]>([])
  const [savingActors, setSavingActors] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [successMessage, setSuccessMessage] = useState<string>('')
  const [messageCommentId, setMessageCommentId] = useState<string | null>(null)
  const [actors, setActors] = useState<string[]>([])
  const [loadingActors, setLoadingActors] = useState(false)

  const loadActors = async () => {
    try {
      setLoadingActors(true)
      const res = await TicketsService.getActors()
      setActors(res.data.actors || [])
    } catch (err) {
      console.error('Failed to load actors:', err)
    } finally {
      setLoadingActors(false)
    }
  }

  const load = async () => {
    if (!id) return
    setLoading(true)
    try {
      const tRes = await TicketsService.getTicketById(id)
      setTicket(tRes.data)
      setComments((tRes.data?.comments as TicketComment[]) || [])
      setErrorMessage('')
      setSuccessMessage('')
      setMessageCommentId(null)
    } catch (err: any) {
      if (err?.response?.status === 403) {
        setErrorMessage('You are not allowed.')
        setSuccessMessage('')
        setMessageCommentId(null)
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    loadActors()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  useEffect(() => {
    if (!id) return
    const intervalId = setInterval(() => {
      load()
    }, 10000)
    return () => clearInterval(intervalId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const submitComment = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!id || !commentText.trim()) return
    const selectedFiles = files ? Array.from(files) : []
    const actorsArr = actorsSelect

    try {
      await TicketsService.createComment({
        Text: commentText,
        TicketId: id,
        Actors: actorsArr,
        Files: selectedFiles,
      })
      setErrorMessage('')
      setCommentText('')
      setActorsSelect([])
      setFiles(null)
      await load()
    } catch (err: any) {
      if (err?.response?.status === 403) {
        setErrorMessage('You are not allowed.')
      }
    }
  }

  const openEditActors = (comment: TicketComment) => {
    setEditingCommentId(comment.id)
    // we do not receive actors per comment in current type; start empty
    setEditingActors([])
    setErrorMessage('')
    setSuccessMessage('')
    setMessageCommentId(comment.id)
  }

  const closeEditActors = () => {
    setEditingCommentId(null)
    setEditingActors([])
    // keep messages visible for the comment container
  }

  const saveActors = async () => {
    if (!editingCommentId) return
    try {
      setSavingActors(true)
      await TicketsService.updateCommentVisibility(editingCommentId, editingActors)
      setErrorMessage('')
      setSuccessMessage('Updated successfully.')
      setMessageCommentId(editingCommentId)
      // await load()
    } catch (err: any) {
      if (err?.response?.status === 403) {
        setErrorMessage('You are not allowed.')
        setSuccessMessage('')
        setMessageCommentId(editingCommentId)
      }
    } finally {
      setSavingActors(false)
    }
  }

  // console.log({
  //   comments,
  // })

  return (
    <KTCard>
      <KTCardBody>
        {loading && <div>Loading...</div>}
        {ticket && (
          <div className='mb-10'>
            <div className='d-flex justify-content-between align-items-center mb-3'>
              <h3 className='mb-0'>{ticket.title}</h3>
              <button
                type='button'
                className='btn btn-sm btn-light-info'
                onClick={() => navigate(`/tickets/${id}/history`)}
              >
                View History
              </button>
            </div>
            <div className='text-muted mb-2'>{ticket.description}</div>
            <div className='d-flex gap-4 flex-wrap'>
              <div>
                <strong>Priority:</strong> {ticket.priority}
              </div>
              <div>
                <strong>Status:</strong> {ticket.statusName}
              </div>
              <div>
                <strong>Product:</strong> {ticket.productType}
              </div>
              <div>
                <strong>Assigned To:</strong> {ticket.assignedTo || 'Unassigned'}
              </div>
              <div>
                <strong>Updated:</strong> {new Date(ticket.updatedAt).toLocaleString()}
              </div>
            </div>
          </div>
        )}

        <div className='mb-10'>
          <h4 className='mb-4'>Comments</h4>
          {comments && comments.length > 0 ? (
            <div className='vstack gap-4'>
              {comments.map((c) => (
                <div key={c.id} className='border rounded p-4'>
                  {messageCommentId === c.id && (
                    <>
                      {errorMessage && (
                        <div className='alert alert-danger mb-3'>{errorMessage}</div>
                      )}
                      {successMessage && (
                        <div className='alert alert-success mb-3'>{successMessage}</div>
                      )}
                    </>
                  )}
                  <div className='d-flex align-items-start mb-2'>
                    <div className='flex-grow-1'>
                      <div className='fw-bold'>{c.user?.fullName || c.userId || 'User'}</div>
                      {c.user?.email && <div className='text-muted small'>{c.user.email}</div>}
                      {c.user?.phoneNumber && (
                        <div className='text-muted small'>{c.user.phoneNumber}</div>
                      )}
                    </div>
                    <div className='flex-grow-1 text-center text-muted'>
                      {new Date(c.createdAt).toLocaleString()}
                    </div>
                    <div className='flex-grow-1 d-flex justify-content-end'>
                      <button
                        type='button'
                        className='btn btn-sm btn-light-primary'
                        onClick={() => openEditActors(c)}
                      >
                        edit actors
                      </button>
                    </div>
                  </div>
                  <div className='mb-3'>{c.text}</div>
                  {editingCommentId === c.id && (
                    <div className='border rounded p-3 bg-light mb-3'>
                      <div className='mb-2 fw-bold'>Edit actors</div>
                      <select
                        multiple
                        className='form-select mb-3'
                        value={editingActors}
                        onChange={(e) =>
                          setEditingActors(Array.from(e.target.selectedOptions).map((o) => o.value))
                        }
                        disabled={loadingActors}
                      >
                        {loadingActors ? (
                          <option>Loading actors...</option>
                        ) : (
                          actors.map((actor) => (
                            <option key={actor} value={actor}>
                              {actor}
                            </option>
                          ))
                        )}
                      </select>
                      <div className='d-flex gap-2'>
                        <button
                          type='button'
                          className='btn btn-sm btn-primary'
                          onClick={saveActors}
                          disabled={savingActors}
                        >
                          {savingActors ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          type='button'
                          className='btn btn-sm btn-light'
                          onClick={closeEditActors}
                          disabled={savingActors}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                  {c.attachments && c.attachments.length > 0 && (
                    <div>
                      <strong>Attachments:</strong>
                      <div className='d-flex flex-wrap gap-3 mt-2'>
                        {c.attachments.map((a) => (
                          <div key={a.id} className='border rounded p-2'>
                            <img src={a.path} alt={a.fileName} style={{objectFit: 'contain'}} />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className='text-muted'>No comments</div>
          )}
        </div>

        <form onSubmit={submitComment} className='vstack gap-3'>
          <h5>Add Comment</h5>
          <textarea
            className='form-control'
            rows={3}
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            placeholder='Write a comment'
          />
          <div className='mb-3'>
            <label className='form-label'>Actors</label>
            {loadingActors ? (
              <div className='text-muted'>Loading actors...</div>
            ) : (
              <div className='d-flex flex-wrap gap-2'>
                {actors.map((actor) => (
                  <button
                    key={actor}
                    type='button'
                    className={`btn btn-sm ${
                      actorsSelect.includes(actor)
                        ? 'btn-primary'
                        : 'btn-outline-secondary text-black'
                    }`}
                    onClick={() => {
                      if (actorsSelect.includes(actor)) {
                        setActorsSelect(actorsSelect.filter((a) => a !== actor))
                      } else {
                        setActorsSelect([...actorsSelect, actor])
                      }
                    }}
                  >
                    {actor}
                  </button>
                ))}
              </div>
            )}
          </div>
          <input
            type='file'
            className='form-control'
            multiple
            onChange={(e) => setFiles(e.target.files)}
          />
          <div>
            <button type='submit' className='btn btn-primary'>
              Submit Comment
            </button>
          </div>
        </form>
      </KTCardBody>
    </KTCard>
  )
}

export default TicketDetails
